// Copyright (c) StrangeBee 2024

import { RandomAntColors } from '@Constants/colors';
import { PatternType } from '@Constants/patternsRoute';
import {
    WidgetIntervalType,
    WidgetOutput,
    WidgetSeriesOutput,
    ObjectTypeV1,
    DescribeFieldAttribute,
    DescribeOutput,
    CustomStatusOutput,
    DefinitionOutput,
    getDateRange,
} from 'thehive-sdk';
import moment from 'moment';
import { AvailableLanguages } from '@Lang/LanguageSwitcherContext';
import { isPeriodValue } from '@Organisms/Dashboard/DashboardPanel/DashboardPeriod/DashboardPeriod';
import { UseLocalMessageType } from '@Helpers/locale';

export function parseResult<I extends Record<string, any>>(data: I, parser: (dataParsed: [string, any]) => void): void {
    Object.entries(data)
        .sort(([dateA = ''], [dateB = '']) => parseInt(dateA) - parseInt(dateB))
        .forEach(parser);
}

export const parseSerieForLabel = (aggKey: string, serie?: WidgetSeriesOutput): string => {
    const { field = '', label = '', agg = '' } = serie || {};

    return field && !label ? `${agg} of ${field}` : label || aggKey;
};

export const parseContent = (
    content: any, //eslint-disable-line
    field: string,
    onFinish: (item: { name: string; value: number }) => void,
    describeData?: DescribeFieldAttribute,
): void => {
    const parsedContent = content[`field_${field}`];

    if (!parsedContent) {
        return;
    }

    Object.entries(parsedContent).forEach(([fieldValue, contentValue]: [string, any]) => {
        const label = describeData?.labels[describeData?.values.findIndex((value) => parseInt(fieldValue) === value)];
        const value = contentValue.count;

        onFinish({
            name: label || fieldValue,
            value,
        });
    });
};

type WidgetDataCommonOutput = { color?: string; name: string; key: string; [key: string]: any };

export const parseSeriesColor = (data: WidgetDataCommonOutput[], indexInitialValue = 0): string[] => {
    const names = Array.from(new Set(data.map(({ key }) => key)));

    return names.map((key, index) => {
        return (
            data.find(({ key: dataName }) => dataName === key)?.color ||
            RandomAntColors[(indexInitialValue + index) % RandomAntColors.length]
        );
    });
};

export const mapEntityToPath: Partial<Record<ObjectTypeV1, PatternType>> = {
    case: 'cases.default',
};

export const createWidgetQueryKey = (widget: WidgetOutput): string => `get-widget-${widget.type}-${widget.id}`;

interface FilterOnClickParams {
    describeData?: DescribeOutput;
    entity: ObjectTypeV1;
    field: string;
    value?: string;
    onClick: (value: any, describeField: DescribeFieldAttribute) => void;
}

export const filterOnClick = ({ describeData, entity, field, value, onClick }: FilterOnClickParams): void => {
    const describeField = describeData?.[entity]?.attributes.find(({ name }) => name === field);

    if (value === undefined || !describeField) {
        return;
    }

    const processedValue = describeField?.values.length
        ? describeField?.values[describeField?.labels.findIndex((label) => label === value)]
        : describeField?.type === 'number'
        ? parseInt(value)
        : value;

    onClick(processedValue || value, describeField);
};

export const widgetSerieTypeValues = ['line', 'area', 'spline', 'area-spline', 'bar'] as const;

export type WidgetSerieType = (typeof widgetSerieTypeValues)[number];

export const isWidgetSerieType = (type: string): type is WidgetSerieType => widgetSerieTypeValues.includes(type as any);

export const convertTypeToAntV = (type: WidgetSerieType): 'line' | 'area' | 'column' =>
    type === 'area-spline' ? 'area' : type === 'spline' ? 'line' : type === 'bar' ? 'column' : type;

interface DateFormatParams {
    date: string;
    interval: WidgetIntervalType;
    language: AvailableLanguages;
}

export const dateFormat = ({ date, language, interval }: DateFormatParams): string =>
    moment(new Date(date).valueOf())
        .locale(language)
        .format(interval === '1M' ? 'MM/YYYY' : interval === '1y' ? 'YYYY' : 'L')
        .toString();

interface ColorFormatParams {
    entity?: ObjectTypeV1;
    field: string;
    customStatusList?: CustomStatusOutput[];
}

export const colorFormat = ({
    entity,
    field,
    customStatusList,
}: ColorFormatParams): ((_ref: Record<string, string>) => string) | undefined =>
    (field === 'status' && entity) ||
    (field === 'value' && entity && [ObjectTypeV1.CASE_STATUS, ObjectTypeV1.ALERT_STATUS].includes(entity))
        ? (_ref) => customStatusList?.find(({ value }) => value === _ref.name)?.colour || ''
        : undefined;

export const parsePeriod = (
    customPeriod: DefinitionOutput['customPeriod'],
    period: string,
): { from?: number; to?: number } => {
    if (period === 'all' || !isPeriodValue(period)) {
        return {};
    }

    if (period === 'custom') {
        const from = moment(customPeriod?.fromDate);
        const to = moment(customPeriod?.toDate);

        return {
            from:
                from
                    .hours(from.hours() || 0)
                    .minutes(from.minutes() || 0)
                    .seconds(from.seconds() || 0)
                    .milliseconds(from.milliseconds() || 0)
                    .valueOf() || 0,
            to:
                to
                    ?.hours(to.hours() || 23)
                    .minutes(to.minutes() || 59)
                    .seconds(to.seconds() || 59)
                    .milliseconds(to.milliseconds() || 999)
                    .valueOf() || 0,
        };
    }

    const [from, to] = getDateRange(period.toLowerCase());

    return {
        from,
        to,
    };
};

export const parseDurationField = (field: string, value: any, i: UseLocalMessageType) => {
    const lowerCaseField = field.toLowerCase();
    const ceiledValue = Math.floor(value);

    if (lowerCaseField.search('day') != -1) {
        return i('app.date.days', { number: ceiledValue });
    }

    if (lowerCaseField.search('hour') != -1) {
        return i('app.date.hours', { number: ceiledValue });
    }

    if (lowerCaseField.search('minute') != -1) {
        return i('app.date.minutes', { number: ceiledValue });
    }

    if (lowerCaseField.search('second') != -1) {
        return i('app.date.seconds', { number: ceiledValue });
    }

    return value;
};
